// ===========================================
// CPG DEMAND MODEL — pure math, no fetching
// ===========================================
// Shared by the CPG Forecast and Margins views and the Production outlook.
// Wholesale order history → units per product per month, the channel split,
// trailing run rates, standing-order commitments, and what to produce next.
// Every number traces to recorded orders, the Inventory sheet, or the cost per
// case the operator typed in — nothing is model-predicted or backfilled.

import { litersPerUnit, parseSku, CATEGORY_LABELS } from '../utils/cpg';

// The statuses the wholesale summaries count — a draft isn't demand yet and a
// cancellation never was.
export const COUNTED_STATUSES = ['confirmed', 'partially_fulfilled', 'fulfilled', 'invoiced'];

// Standing weekly orders repeat ~4.35 times a month (52 weeks / 12 months).
export const WEEKS_PER_MONTH = 52 / 12;

// ---------- month keys ("2026-11") ----------
export const ymOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
export const addMonths = (ym: string, n: number) => {
  const [y, m] = ym.split('-').map(Number);
  return ymOf(new Date(y, (m || 1) - 1 + n, 1));
};
export const ymLong = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, (m || 1) - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};
export const ymShort = (ym: string, withYear = false) => {
  const [y, m] = ym.split('-').map(Number);
  const name = new Date(y, (m || 1) - 1, 1).toLocaleDateString('en-US', { month: 'short' });
  return withYear ? `${name} ’${String(y).slice(2)}` : name;
};

// ---------- shapes ----------
export interface ProductDemand {
  key: string;                 // menuItemId when known, else the line-item name
  name: string;
  menuItemId: string | null;
  sku: string | null;
  litersEach: number | null;   // parsed from the name ("500ml", "12pk 250ml")
  unitsByMonth: Record<string, number>;
  unitsTotal: number;
  // Margin math stays in USD — costs are USD; other currencies are said, not blended.
  usdUnits: number;
  usdRevenueCents: number;
  usdUnitsByMonth: Record<string, number>;
  usdRevenueByMonth: Record<string, number>;
  revenueByCurrency: Record<string, number>;
  runRate: number | null;      // units/month over the trailing complete months
  onHand: number | null;       // Inventory currentQuantity when a record matches
  produce: number | null;      // max(0, ceil(runRate − onHand)) when both known
  costCents: number | null;    // per unit (case)
  costSource: 'case' | 'inventory' | null;
}

export interface ChannelDemand {
  category: string;
  label: string;
  unitsByMonth: Record<string, number>;
  unitsTotal: number;
  revenueByCurrency: Record<string, number>;
}

export interface DemandModel {
  months: string[];            // the shown window: ≤12 trailing months + any future months with booked orders
  currentYm: string;
  futureMonths: string[];      // months after the current one that carry booked orders
  basisMonths: string[];       // the trailing complete months the run rate averages (≤3)
  yearlyMonths: string[];      // the trailing complete months the yearly run rate uses (≤12)
  orderCount: number;          // counted orders inside the shown window
  products: ProductDemand[];   // channel-filtered when a channel is passed
  productsAll: ProductDemand[];
  channels: ChannelDemand[];
  unitsByMonth: Record<string, number>;   // totals across all products (no channel filter)
  projection: {
    targetMonth: string;                  // the month underway
    unitsPerMonth: number | null;         // trailing run rate, all products
    litersPerMonth: number | null;
    recordedThisMonth: number;            // counted units already dated in the target month
    bookedNextMonth: number;              // counted units already dated in the month after
    nextQuarterMonths: string[];          // the three months after the current one
    nextQuarterUnits: number | null;
  };
  standing: {
    unitsPerWeek: number;
    unitsPerMonth: number;                // ×52/12, rounded
    customers: number;
    items: Array<{ name: string; unitsPerWeek: number }>;
  };
  yearly: {
    monthsUsed: number;
    unitsAnnualized: number | null;
    litersAnnualized: number | null;
    revenueAnnualizedByCurrency: Record<string, number>;
  };
  nonUsdByCurrency: Record<string, number>;  // counted revenue billed outside USD, natively
  hasAnyCost: boolean;
}

// ---------- internals ----------
// A fulfillment date is a calendar date; letting `new Date('2026-11-01')`
// parse it as UTC midnight would shift it a day west of Greenwich and file a
// Nov 1 order under October. Read the month straight off ISO strings instead
// (the same UTC-month convention the CPG P&L uses for deduction dates).
const orderYm = (o: any): string | null => {
  const raw = o.fulfillmentDate || o.createdAt;
  if (!raw) return null;
  const iso = /^(\d{4})-(\d{2})-\d{2}/.exec(String(raw));
  if (iso) return `${iso[1]}-${iso[2]}`;
  const when = new Date(raw);
  return Number.isFinite(when.getTime()) ? when.toISOString().slice(0, 7) : null;
};

const lineUnits = (it: any) => parseFloat(String(it.quantity)) || 0;
const lineRevenue = (it: any) => Number(it.totalPriceCents) || (Number(it.unitPriceCents) || 0) * lineUnits(it);

function aggregateProducts(
  orders: Array<{ o: any; ym: string }>,
  menuById: Map<string, any>,
  costPerCase: Record<string, number>,
  invBySku: Map<string, any>,
  invByName: Map<string, any>,
): ProductDemand[] {
  const agg = new Map<string, ProductDemand>();
  for (const { o, ym } of orders) {
    const cur = String(o.currencyCode || 'USD').toUpperCase();
    for (const it of o.items || []) {
      const units = lineUnits(it);
      if (!units) continue;
      const key = it.menuItemId || it.name;
      let row = agg.get(key);
      if (!row) {
        const menu = it.menuItemId ? menuById.get(it.menuItemId) : null;
        const name = menu?.name || it.name || 'Unnamed product';
        const sku = menu ? parseSku(menu.description) : null;
        const inv = (sku ? invBySku.get(sku.toLowerCase()) : undefined)
          || invByName.get(String(name).trim().toLowerCase()) || null;
        const caseCost = it.menuItemId ? costPerCase[it.menuItemId] : undefined;
        const invCostCents = inv && parseFloat(String(inv.unitCost)) > 0 ? Math.round(parseFloat(String(inv.unitCost)) * 100) : null;
        row = {
          key, name, menuItemId: it.menuItemId || null, sku,
          litersEach: litersPerUnit(name),
          unitsByMonth: {}, unitsTotal: 0,
          usdUnits: 0, usdRevenueCents: 0, usdUnitsByMonth: {}, usdRevenueByMonth: {}, revenueByCurrency: {},
          runRate: null,
          onHand: inv ? (parseFloat(String(inv.currentQuantity)) || 0) : null,
          produce: null,
          costCents: caseCost && caseCost > 0 ? caseCost : invCostCents,
          costSource: caseCost && caseCost > 0 ? 'case' : invCostCents != null ? 'inventory' : null,
        };
        agg.set(key, row);
      }
      const rev = lineRevenue(it);
      row.unitsByMonth[ym] = (row.unitsByMonth[ym] || 0) + units;
      row.unitsTotal += units;
      row.revenueByCurrency[cur] = (row.revenueByCurrency[cur] || 0) + rev;
      if (cur === 'USD') {
        row.usdUnits += units;
        row.usdRevenueCents += rev;
        row.usdUnitsByMonth[ym] = (row.usdUnitsByMonth[ym] || 0) + units;
        row.usdRevenueByMonth[ym] = (row.usdRevenueByMonth[ym] || 0) + rev;
      }
    }
  }
  return [...agg.values()].sort((a, b) => b.unitsTotal - a.unitsTotal);
}

const avgOver = (byMonth: Record<string, number>, months: string[]): number | null =>
  months.length === 0 ? null : months.reduce((s, m) => s + (byMonth[m] || 0), 0) / months.length;

// ---------- the builder ----------
export function buildDemandModel(args: {
  orders: any[];
  customers: any[];
  menuItems: any[];
  inventory: any[];
  costPerCase: Record<string, number>;
  channel?: string | null;       // filter the `products` cut to one sales category
  entity?: 'us' | 'eu' | 'all';  // standing commitments respect the entity lens
  now?: Date;
}): DemandModel {
  const { orders, customers, menuItems, inventory, costPerCase, channel, entity = 'all' } = args;
  const now = args.now || new Date();
  const currentYm = ymOf(now);

  const custById = new Map<string, any>((customers || []).map((c: any) => [c.id, c]));
  const categoryOf = (o: any): string => custById.get(o.customerId)?.category || 'uncategorized';

  // Counted orders with a resolvable month, inside the window: the trailing 12
  // calendar months (or as much history as exists) plus any future months that
  // already carry booked orders.
  const dated = (orders || [])
    .filter((o: any) => COUNTED_STATUSES.includes(o.status))
    .map((o: any) => ({ o, ym: orderYm(o) as string }))
    .filter((x) => !!x.ym);
  const windowStart = addMonths(currentYm, -11);
  const inWindow = dated.filter((x) => x.ym >= windowStart);

  const monthSet = new Set<string>();
  for (const x of inWindow) monthSet.add(x.ym);
  const firstYm = [...monthSet].sort()[0] || currentYm;
  const months: string[] = [];
  for (let m = firstYm <= currentYm ? firstYm : currentYm; m <= currentYm; m = addMonths(m, 1)) months.push(m);
  const futureMonths = [...monthSet].filter((m) => m > currentYm).sort();
  months.push(...futureMonths);

  // Lookups
  const menuById = new Map<string, any>((menuItems || []).map((m: any) => [m.id, m]));
  const invBySku = new Map<string, any>();
  const invByName = new Map<string, any>();
  for (const i of inventory || []) {
    if (i.sku) invBySku.set(String(i.sku).trim().toLowerCase(), i);
    invByName.set(String(i.name || '').trim().toLowerCase(), i);
  }

  const productsAll = aggregateProducts(inWindow, menuById, costPerCase, invBySku, invByName);
  const products = channel
    ? aggregateProducts(inWindow.filter((x) => categoryOf(x.o) === channel), menuById, costPerCase, invBySku, invByName)
    : productsAll;

  // Channel split (never channel-filtered — it IS the split)
  const chMap = new Map<string, ChannelDemand>();
  for (const { o, ym } of inWindow) {
    const cat = categoryOf(o);
    const row = chMap.get(cat) || {
      category: cat,
      label: CATEGORY_LABELS[cat] || (cat === 'uncategorized' ? 'Uncategorized' : cat),
      unitsByMonth: {}, unitsTotal: 0, revenueByCurrency: {},
    };
    const cur = String(o.currencyCode || 'USD').toUpperCase();
    for (const it of o.items || []) {
      const units = lineUnits(it);
      row.unitsByMonth[ym] = (row.unitsByMonth[ym] || 0) + units;
      row.unitsTotal += units;
      row.revenueByCurrency[cur] = (row.revenueByCurrency[cur] || 0) + lineRevenue(it);
    }
    chMap.set(cat, row);
  }
  const channels = [...chMap.values()].sort((a, b) => b.unitsTotal - a.unitsTotal);

  // Totals by month
  const unitsByMonth: Record<string, number> = {};
  for (const p of productsAll) for (const [m, u] of Object.entries(p.unitsByMonth)) unitsByMonth[m] = (unitsByMonth[m] || 0) + u;

  // Run rates — averaged over the trailing complete months only (the month
  // underway would drag any average down).
  const completeMonths = months.filter((m) => m < currentYm);
  const basisMonths = completeMonths.slice(-3);
  const yearlyMonths = completeMonths.slice(-12);
  for (const list of channel ? [products, productsAll] : [productsAll]) {
    for (const p of list) {
      const rate = avgOver(p.unitsByMonth, basisMonths);
      p.runRate = rate;
      p.produce = rate != null && p.onHand != null ? Math.max(0, Math.ceil(rate - p.onHand)) : null;
    }
  }

  const unitsPerMonth = avgOver(unitsByMonth, basisMonths);
  const litersPerMonth = basisMonths.length
    ? productsAll.reduce((s, p) => s + (p.litersEach != null ? p.litersEach * ((avgOver(p.unitsByMonth, basisMonths) || 0)) : 0), 0)
    : null;
  const nextQuarterMonths = [1, 2, 3].map((n) => addMonths(currentYm, n));

  // Standing weekly orders — committed volume, drafted every week.
  let standingUnitsPerWeek = 0;
  let standingCustomers = 0;
  const standingItems = new Map<string, number>();
  for (const c of customers || []) {
    if (c.isActive === false || !c.standingWeekly) continue;
    if (entity !== 'all' && (c.entity || 'us') !== entity) continue;
    standingCustomers += 1;
    for (const it of c.standingWeekly.items || []) {
      const qty = Number(it.quantity) || 0;
      standingUnitsPerWeek += qty;
      const name = menuById.get(it.menuItemId)?.name || 'product';
      standingItems.set(name, (standingItems.get(name) || 0) + qty);
    }
  }

  // Yearly run rate — the trailing ≤12 complete months, annualized and labeled.
  const yearlyUnits = yearlyMonths.reduce((s, m) => s + (unitsByMonth[m] || 0), 0);
  const yearlyLiters = yearlyMonths.length
    ? productsAll.reduce((s, p) => s + (p.litersEach != null ? p.litersEach * yearlyMonths.reduce((t, m) => t + (p.unitsByMonth[m] || 0), 0) : 0), 0)
    : 0;
  const revenueAnnualizedByCurrency: Record<string, number> = {};
  if (yearlyMonths.length > 0) {
    const yearlySet = new Set(yearlyMonths);
    for (const { o, ym } of inWindow) {
      if (!yearlySet.has(ym)) continue;
      const cur = String(o.currencyCode || 'USD').toUpperCase();
      revenueAnnualizedByCurrency[cur] = (revenueAnnualizedByCurrency[cur] || 0) + (o.totalCents || 0);
    }
    for (const cur of Object.keys(revenueAnnualizedByCurrency)) {
      revenueAnnualizedByCurrency[cur] = Math.round((revenueAnnualizedByCurrency[cur] / yearlyMonths.length) * 12);
    }
  }

  // Non-USD revenue across the window, natively — said, never blended.
  const nonUsdByCurrency: Record<string, number> = {};
  for (const { o } of inWindow) {
    const cur = String(o.currencyCode || 'USD').toUpperCase();
    if (cur === 'USD') continue;
    nonUsdByCurrency[cur] = (nonUsdByCurrency[cur] || 0) + (o.totalCents || 0);
  }

  return {
    months, currentYm, futureMonths, basisMonths, yearlyMonths,
    orderCount: inWindow.length,
    products, productsAll, channels, unitsByMonth,
    projection: {
      targetMonth: currentYm,
      unitsPerMonth: unitsPerMonth != null ? Math.round(unitsPerMonth) : null,
      litersPerMonth: litersPerMonth != null ? Math.round(litersPerMonth * 10) / 10 : null,
      recordedThisMonth: Math.round(unitsByMonth[currentYm] || 0),
      bookedNextMonth: Math.round(unitsByMonth[addMonths(currentYm, 1)] || 0),
      nextQuarterMonths,
      nextQuarterUnits: unitsPerMonth != null ? Math.round(unitsPerMonth * 3) : null,
    },
    standing: {
      unitsPerWeek: Math.round(standingUnitsPerWeek),
      unitsPerMonth: Math.round(standingUnitsPerWeek * WEEKS_PER_MONTH),
      customers: standingCustomers,
      items: [...standingItems.entries()].map(([name, unitsPerWeek]) => ({ name, unitsPerWeek })).sort((a, b) => b.unitsPerWeek - a.unitsPerWeek),
    },
    yearly: {
      monthsUsed: yearlyMonths.length,
      unitsAnnualized: yearlyMonths.length ? Math.round((yearlyUnits / yearlyMonths.length) * 12) : null,
      litersAnnualized: yearlyMonths.length ? Math.round((yearlyLiters / yearlyMonths.length) * 12) : null,
      revenueAnnualizedByCurrency,
    },
    nonUsdByCurrency,
    hasAnyCost: productsAll.some((p) => p.costCents != null),
  };
}
